/**
 * PricingPagesPage.tsx — Admin page for creating and managing dynamic pricing pages.
 *
 * Users configure which plans appear, badges ("Most Popular"), signup URLs,
 * and can preview the rendered HTML or copy the JSON/HTML API endpoints.
 *
 * @since 2026-06-20
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';

interface PricingPage {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  signupUrl: string | null;
  ctaText: string;
  isDefault: boolean;
  isPublished: boolean;
  themeConfig: Record<string, string>;
  customCss: string | null;
  createdAt: string;
}

interface Plan {
  id: string;
  name: string;
  description: string | null;
  interval: string;
  isActive: boolean;
}

interface PagePlan {
  id: string;
  planId: string;
  displayOrder: number;
  badge: string | null;
  isHighlighted: boolean;
  ctaTextOverride: string | null;
  signupUrlOverride: string | null;
}

export default function PricingPagesPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const apiBase = `${apiBaseUrl}/api/v1/orgs/${activeOrg?.orgId}`;
  const orgId = activeOrg?.orgId ?? '';
  const queryClient = useQueryClient();
  const [selectedPage, setSelectedPage] = useState<PricingPage | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createData, setCreateData] = useState({ slug: '', title: '', subtitle: '', signupUrl: '', ctaText: 'Get Started' });
  const [showPreview, setShowPreview] = useState(false);

  // Fetch all pricing pages
  const { data: pagesData } = useQuery({
    queryKey: ['pricing-pages'],
    queryFn: async () => {
      const res = await fetch(`${apiBase}/pricing-pages`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load');
      return res.json() as Promise<{ pages: PricingPage[] }>;
    },
  });

  // Fetch all plans (for assignment)
  const { data: plansData } = useQuery({
    queryKey: ['plans-list'],
    queryFn: async () => {
      const res = await fetch(`${apiBase}/plans`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load plans');
      return res.json() as Promise<{ plans: Plan[] }>;
    },
  });

  // Fetch page plans when a page is selected
  const { data: pagePlansData } = useQuery({
    queryKey: ['pricing-page-plans', selectedPage?.id],
    queryFn: async () => {
      if (!selectedPage) return { plans: [] };
      const res = await fetch(`${apiBase}/pricing-pages/${selectedPage.id}/plans`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      return res.json() as Promise<{ plans: PagePlan[] }>;
    },
    enabled: !!selectedPage,
  });

  const createPageMutation = useMutation({
    mutationFn: async (input: typeof createData) => {
      const res = await fetch(`${apiBase}/pricing-pages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to create');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-pages'] });
      setShowCreateForm(false);
      setCreateData({ slug: '', title: '', subtitle: '', signupUrl: '', ctaText: 'Get Started' });
    },
  });

  const updatePageMutation = useMutation({
    mutationFn: async ({ id, ...input }: { id: string } & Record<string, unknown>) => {
      const res = await fetch(`${apiBase}/pricing-pages/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to update');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pricing-pages'] }),
  });

  const setPagePlansMutation = useMutation({
    mutationFn: async ({ pageId, plans }: { pageId: string; plans: { planId: string; displayOrder: number; badge?: string; isHighlighted?: boolean }[] }) => {
      const res = await fetch(`${apiBase}/pricing-pages/${pageId}/plans`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plans }),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pricing-page-plans'] }),
  });

  const deletePageMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${apiBase}/pricing-pages/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-pages'] });
      setSelectedPage(null);
    },
  });

  const pages = pagesData?.pages ?? [];
  const allPlans = plansData?.plans ?? [];
  const pagePlans = pagePlansData?.plans ?? [];
  const assignedPlanIds = new Set(pagePlans.map(p => p.planId));

  const baseUrl = apiBase.replace(/\/api\/v1\/orgs\/[^/]+$/, '');
  const publicJsonUrl = selectedPage ? `${baseUrl}/pricing/${orgId}/${selectedPage.slug}` : '';
  const publicHtmlUrl = selectedPage ? `${baseUrl}/pricing/${orgId}/${selectedPage.slug}/html` : '';

  return (
    <AdminLayout title="Pricing Pages">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Pricing Pages</h1>
            <p style={{ color: '#64748B', fontSize: '0.9rem', marginTop: 4 }}>
              Create dynamic pricing pages. Expose as JSON API or fully-rendered HTML.
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{ background: '#0F766E', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}
          >
            + New Pricing Page
          </button>
        </div>

        {showCreateForm && (
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <h3 style={{ marginBottom: 16 }}>Create Pricing Page</h3>
            <form onSubmit={e => { e.preventDefault(); createPageMutation.mutate(createData); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>Slug *</label>
                  <input value={createData.slug} onChange={e => setCreateData(d => ({ ...d, slug: e.target.value }))} required style={inputStyle} placeholder="e.g. default, enterprise" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>Title *</label>
                  <input value={createData.title} onChange={e => setCreateData(d => ({ ...d, title: e.target.value }))} required style={inputStyle} placeholder="Choose Your Plan" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>Subtitle</label>
                  <input value={createData.subtitle} onChange={e => setCreateData(d => ({ ...d, subtitle: e.target.value }))} style={inputStyle} placeholder="Start free, upgrade anytime" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>Signup URL</label>
                  <input value={createData.signupUrl} onChange={e => setCreateData(d => ({ ...d, signupUrl: e.target.value }))} style={inputStyle} placeholder="https://app.example.com/signup" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>CTA Button Text</label>
                  <input value={createData.ctaText} onChange={e => setCreateData(d => ({ ...d, ctaText: e.target.value }))} style={inputStyle} placeholder="Get Started" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button type="submit" style={btnPrimary}>Create</button>
                <button type="button" onClick={() => setShowCreateForm(false)} style={btnSecondary}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: selectedPage ? '300px 1fr' : '1fr', gap: 24 }}>
          {/* Pages list */}
          <div>
            {pages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, background: '#F8FAFC', borderRadius: 12, border: '1px dashed #CBD5E1' }}>
                <p style={{ fontWeight: 600, marginBottom: 8 }}>No pricing pages yet</p>
                <p style={{ color: '#64748B', fontSize: '0.9rem' }}>Create your first dynamic pricing page.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pages.map(p => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPage(p)}
                    style={{
                      padding: '12px 16px', background: selectedPage?.id === p.id ? '#F0FDFA' : '#fff',
                      border: `1px solid ${selectedPage?.id === p.id ? '#0F766E' : '#E2E8F0'}`,
                      borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600 }}>{p.title}</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {p.isDefault && <span style={{ background: '#0F766E', color: '#fff', fontSize: '0.65rem', padding: '2px 6px', borderRadius: 4 }}>DEFAULT</span>}
                        {p.isPublished && <span style={{ background: '#16A34A', color: '#fff', fontSize: '0.65rem', padding: '2px 6px', borderRadius: 4 }}>LIVE</span>}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748B' }}>/{p.slug}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Page detail */}
          {selectedPage && (
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                <div>
                  <h2 style={{ margin: 0 }}>{selectedPage.title}</h2>
                  {selectedPage.subtitle && <p style={{ color: '#64748B', marginTop: 4 }}>{selectedPage.subtitle}</p>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {!selectedPage.isPublished && (
                    <button onClick={() => updatePageMutation.mutate({ id: selectedPage.id, isPublished: true })} style={btnPrimary}>
                      Publish
                    </button>
                  )}
                  {selectedPage.isPublished && (
                    <button onClick={() => updatePageMutation.mutate({ id: selectedPage.id, isPublished: false })} style={btnSecondary}>
                      Unpublish
                    </button>
                  )}
                  <button onClick={() => setShowPreview(!showPreview)} style={btnSecondary}>
                    {showPreview ? 'Hide Preview' : 'Preview'}
                  </button>
                  <button onClick={() => { if (confirm('Delete this pricing page?')) deletePageMutation.mutate(selectedPage.id); }} style={{ ...btnSecondary, color: '#DC2626', borderColor: '#FCA5A5' }}>
                    Delete
                  </button>
                </div>
              </div>

              {/* API Endpoints */}
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Public Endpoints {!selectedPage.isPublished && '(publish to enable)'}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ background: '#E2E8F0', padding: '4px 8px', borderRadius: 4, fontSize: '0.8rem', flex: 1 }}>
                      GET {publicJsonUrl}
                    </code>
                    <button onClick={() => navigator.clipboard.writeText(publicJsonUrl)} style={{ fontSize: '0.75rem', cursor: 'pointer', border: '1px solid #CBD5E1', borderRadius: 4, padding: '2px 8px', background: 'none' }}>
                      Copy
                    </button>
                    <span style={{ fontSize: '0.75rem', color: '#64748B' }}>JSON</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ background: '#E2E8F0', padding: '4px 8px', borderRadius: 4, fontSize: '0.8rem', flex: 1 }}>
                      GET {publicHtmlUrl}
                    </code>
                    <button onClick={() => navigator.clipboard.writeText(publicHtmlUrl)} style={{ fontSize: '0.75rem', cursor: 'pointer', border: '1px solid #CBD5E1', borderRadius: 4, padding: '2px 8px', background: 'none' }}>
                      Copy
                    </button>
                    <span style={{ fontSize: '0.75rem', color: '#64748B' }}>HTML</span>
                  </div>
                </div>
              </div>

              {/* Signup URL Config */}
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Signup / CTA Configuration</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#64748B' }}>Signup URL (appended with ?plan=ID)</label>
                    <input
                      value={selectedPage.signupUrl ?? ''}
                      onChange={e => updatePageMutation.mutate({ id: selectedPage.id, signupUrl: e.target.value })}
                      style={inputStyle}
                      placeholder="https://app.example.com/signup"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#64748B' }}>CTA Button Text</label>
                    <input
                      value={selectedPage.ctaText}
                      onChange={e => updatePageMutation.mutate({ id: selectedPage.id, ctaText: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Plans Assignment */}
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 12 }}>Plans on This Page</h4>
                {pagePlans.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {pagePlans.map(pp => {
                      const plan = allPlans.find(p => p.id === pp.planId);
                      return (
                        <div key={pp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                          <span style={{ fontWeight: 600, flex: 1 }}>{plan?.name ?? pp.planId}</span>
                          {pp.badge && <span style={{ background: '#F59E0B', color: '#fff', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 10 }}>{pp.badge}</span>}
                          {pp.isHighlighted && <span style={{ fontSize: '0.7rem', color: '#0F766E' }}>★ Featured</span>}
                          <input
                            placeholder="Badge (e.g. Most Popular)"
                            defaultValue={pp.badge ?? ''}
                            onBlur={e => {
                              const updated = pagePlans.map(p =>
                                p.planId === pp.planId ? { ...p, badge: e.target.value || undefined } : p
                              );
                              setPagePlansMutation.mutate({ pageId: selectedPage.id, plans: updated.map(p => ({ planId: p.planId, displayOrder: p.displayOrder, badge: p.badge ?? undefined, isHighlighted: p.isHighlighted })) });
                            }}
                            style={{ ...inputStyle, width: 150, fontSize: '0.8rem' }}
                          />
                          <label style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="checkbox"
                              checked={pp.isHighlighted}
                              onChange={e => {
                                const updated = pagePlans.map(p =>
                                  p.planId === pp.planId ? { ...p, isHighlighted: e.target.checked } : p
                                );
                                setPagePlansMutation.mutate({ pageId: selectedPage.id, plans: updated.map(p => ({ planId: p.planId, displayOrder: p.displayOrder, badge: p.badge ?? undefined, isHighlighted: p.isHighlighted })) });
                              }}
                            />
                            Featured
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Add plan */}
                <div>
                  <select
                    onChange={e => {
                      if (!e.target.value) return;
                      const newPlans = [
                        ...pagePlans.map(p => ({ planId: p.planId, displayOrder: p.displayOrder, badge: p.badge ?? undefined, isHighlighted: p.isHighlighted })),
                        { planId: e.target.value, displayOrder: pagePlans.length, isHighlighted: false },
                      ];
                      setPagePlansMutation.mutate({ pageId: selectedPage.id, plans: newPlans });
                      e.target.value = '';
                    }}
                    style={inputStyle}
                  >
                    <option value="">+ Add a plan to this page…</option>
                    {allPlans.filter(p => p.isActive && !assignedPlanIds.has(p.id)).map(p => (
                      <option key={p.id} value={p.id}>{p.name} ({p.interval})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview iframe */}
              {showPreview && selectedPage.isPublished && (
                <div style={{ marginTop: 20 }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Live Preview</h4>
                  <iframe
                    src={publicHtmlUrl}
                    style={{ width: '100%', height: 600, border: '1px solid #E2E8F0', borderRadius: 8 }}
                    title="Pricing page preview"
                  />
                </div>
              )}
              {showPreview && !selectedPage.isPublished && (
                <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: 12, marginTop: 20 }}>
                  <p style={{ fontSize: '0.85rem' }}>⚠️ Publish the page to preview it. The public endpoints only serve published pages.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: '0.9rem',
};
const btnPrimary: React.CSSProperties = {
  background: '#0F766E', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer',
};
const btnSecondary: React.CSSProperties = {
  background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1', borderRadius: 6, padding: '8px 16px', cursor: 'pointer',
};
